import { AppDispatch } from "../../../../../app/store";
import { BookingType } from "../../../../../features/Booking-Form/typings/booking-inputs";
import {
  SelectedZoneOrWorkplace,
  SelectedZoneOrWorkplaceType,
  setSelectedByAttFilter
} from "../../../../../features/Reports/slices/report.slice";
import { FloorPlanMemo } from "../../../../../features/Reports/typings/reports.types";
import {
  GroupFilterSelected,
  GroupFilterType
} from "../../../../BookingForm/BookingFilter/types/GroupFilters.type";
import { filterByBookingFilterGroups } from "../../../../BookingForm/functions/form.functions";
import {
  findPlaceBookingType,
  findZoneBookingType
} from "../../../../FacilityManager/Components/Views/ReportFloorPlanView/ReportFloorPlanView.functions";
import { IFloorPayload } from "../../../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { IPlaceSchedule } from "../../../../FacilityManager/Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../FacilityManager/Domain/Types/FloorPlan/ZoneSchedule";
import { FilterTypState } from "../../DateAndTypeSelector/facility-report-filter-and-selector.component";

export function getFilterEntries(
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace | null,
  attFilterEntries: (IPlaceSchedule | IZoneSchedule)[],
  floorData: FloorPlanMemo | undefined,
  floor: { id: number; open: boolean }
) {
  if (!floorData) return [];
  if (selectedZoneOrWorkplace?.open) {
    const currentFloorData = floorData[floor.id].floorData;
    if (selectedZoneOrWorkplace.type === SelectedZoneOrWorkplaceType.WORKPLACE) {
      return currentFloorData.places.filter(place =>
        selectedZoneOrWorkplace.id.includes(place.inventory?.id || place.inventoryId)
      );
    } else {
      return currentFloorData.zones.filter(zone =>
        selectedZoneOrWorkplace.id.includes(zone.inventory?.id || zone.inventoryId)
      );
    }
  }

  return attFilterEntries;
}

export enum ReportFilterMode {
  SELECTPLACEORZONE = "select-place-or-zone",
  SELECTZONEINCPLACE = "select-zone-include-place", // placezone are selected and assigned places, too
  NORMALFILTER = "normal-filter",
  FLOORREPORT = "floor-report"
}

export function getFilterParams({
  selectedZoneOrWorkplace,
  selectedBookingType,
  floor,
  floorData,
  attFilterEntries,
  attributeSelected
}: {
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace | null;
  selectedBookingType: BookingType | null;
  floor: { id: number; open: boolean };
  floorData: IFloorPayload | undefined;
  attFilterEntries: (IPlaceSchedule | IZoneSchedule)[];
  attributeSelected: GroupFilterSelected[] | undefined;
}): { params: number[]; reportMode: ReportFilterMode } {
  // apply selected zone or workplace first
  if (selectedZoneOrWorkplace?.open && selectedZoneOrWorkplace.floorInventoryId === floor.id) {
    // if normal zones(including places) are selected and filter by att, then places should return for report fetching
    return getFilterParamsBySelectedZnOrWp({
      selectedZoneOrWorkplace,
      selectedBookingType,
      attributeSelected,
      floorData
    });
  }

  if (attributeSelected?.length) {
    return {
      params: attFilterEntries.map(ent => ent.inventoryId || ent.inventory?.id || 0),
      reportMode: ReportFilterMode.NORMALFILTER
    };
  }

  return { params: [floor.id], reportMode: ReportFilterMode.FLOORREPORT };
}

export function getFilterParamsBySelectedZnOrWp({
  selectedZoneOrWorkplace,
  selectedBookingType,
  attributeSelected,
  floorData
}: {
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace;
  selectedBookingType: BookingType | null;
  attributeSelected: GroupFilterSelected[] | undefined;
  floorData: IFloorPayload | undefined;
}) {
  if (
    selectedZoneOrWorkplace.type === SelectedZoneOrWorkplaceType.ZONE &&
    attributeSelected?.length &&
    selectedBookingType !== BookingType.CONFERENCEZONE // noPlaceZone
  ) {
    // sanity check
    if (!floorData) return { params: [], reportMode: ReportFilterMode.FLOORREPORT };

    const zoneScheduleIds = new Set(
      floorData.zones
        .filter(zone => selectedZoneOrWorkplace.id.includes(zone.inventory?.id || zone.inventoryId))
        .map(zone => zone.id)
    );

    // apply filter by attributes
    const equipCate = attributeSelected
      .filter(item => item.type === GroupFilterType.EQUIPMENTCATEGORY)
      .map(i => ({ id: i.id, quantity: i.quantity }));
    const bookProp = attributeSelected
      .filter(item => item.type === GroupFilterType.BOOKINGPROPERTY)
      .map(i => i.id);
    const placeCate = attributeSelected
      .filter(item => item.type === GroupFilterType.PLACECATEGORY)
      .map(i => i.id);
    const zoneCate = attributeSelected
      .filter(item => item.type === GroupFilterType.ZONECATEGORY)
      .map(i => i.id);

    const placeIds = filterByBookingFilterGroups({
      mode: BookingType.PLACEZONE,
      entries: floorData.places.filter(place => zoneScheduleIds.has(place.zoneScheduleId)),
      bookingFilters: {
        equipmentCategories: equipCate,
        propertyIds: bookProp,
        placeCategoryIds: placeCate,
        zoneCategoryIds: zoneCate
      }
    }).map(ent => ent.inventory?.id || ent.inventoryId);

    return { params: placeIds, reportMode: ReportFilterMode.SELECTZONEINCPLACE };
  } else {
    return { params: selectedZoneOrWorkplace.id, reportMode: ReportFilterMode.SELECTPLACEORZONE };
  }
}

export function getFilterAndBookingType(
  attFilterEntries: (IPlaceSchedule | IZoneSchedule)[],
  selectedBookingType: BookingType | null,
  fallbackFilterType: FilterTypState
): { filterTyp: FilterTypState; bookingType: BookingType } {
  if (!attFilterEntries.length) {
    return {
      filterTyp: fallbackFilterType,
      bookingType: selectedBookingType || BookingType.WORKPLACE
    };
  }

  if (selectedBookingType === BookingType.CONFERENCEZONE) {
    const types = (attFilterEntries as IZoneSchedule[]).map(p => p.inventory?.zoneTypeId || 0);
    return {
      filterTyp: SelectedZoneOrWorkplaceType.ZONE,
      bookingType: findZoneBookingType(types[0])
    };
  }

  const types = (attFilterEntries as IPlaceSchedule[]).map(p => p.inventory?.placeTypeId || 0);
  return {
    filterTyp: SelectedZoneOrWorkplaceType.WORKPLACE,
    bookingType: findPlaceBookingType(types[0])
  };
}

export function checkSelectedInComparisonFloor(
  reportSelected: (IPlaceSchedule | IZoneSchedule)[],
  floorData: FloorPlanMemo | undefined,
  currentFloors: {
    id: number;
    open: boolean;
  }[],
  selectedBookingType: BookingType | null
) {
  if (!floorData) return false;

  // check if the second floor is open
  const comparisonFloor = currentFloors.at(-1);
  if (comparisonFloor?.open) {
    if (selectedBookingType === BookingType.CONFERENCEZONE) {
      return floorData[comparisonFloor.id].floorData.zones.some(zone =>
        reportSelected.find(
          sel => (sel.inventoryId || sel.inventory?.id) === zone.inventoryId || zone.inventory?.id
        )
      );
    }

    return floorData[comparisonFloor.id].floorData.places.some(place =>
      reportSelected.find(
        sel => (sel.inventoryId || sel.inventory?.id) === place.inventoryId || place.inventory?.id
      )
    );
  }

  return false;
}

/** to avoid cognitive complexity */
export const getFetchValues = (
  newAttFilterEntries: (IPlaceSchedule | IZoneSchedule)[],
  floor: { id: number; open: boolean },
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace,
  reportMode: ReportFilterMode,
  filterTyp: FilterTypState
) => {
  const fetchReportId = newAttFilterEntries.length ? newAttFilterEntries : floor.id;
  const isSelectedZoneOrWpOpen = selectedZoneOrWorkplace?.open;
  const isZoneSelected =
    isSelectedZoneOrWpOpen && selectedZoneOrWorkplace.type === SelectedZoneOrWorkplaceType.ZONE;
  const isSelectPlaceOrZone = isZoneSelected && reportMode === ReportFilterMode.SELECTPLACEORZONE;

  const calcPerWpValForSelected = isSelectPlaceOrZone;
  const calcPerWpVal = reportMode === ReportFilterMode.FLOORREPORT ? true : calcPerWpValForSelected;

  const filterTypeValForSelected = isSelectPlaceOrZone ? "zone" : filterTyp;
  const filterTypeVal =
    reportMode === ReportFilterMode.FLOORREPORT ? "floor" : filterTypeValForSelected;

  const isAttFilterAppliedVal = !isSelectedZoneOrWpOpen;

  return { calcPerWpVal, filterTypeVal, isAttFilterAppliedVal, fetchReportId };
};

export const handleDispatchSelectedByAttFilter = (
  attFilterEntries: (IPlaceSchedule | IZoneSchedule)[],
  dispatch: AppDispatch,
  filterParams: number[],
  bookingType: BookingType,
  floorId: number
) => {
  if (attFilterEntries.length > 0) {
    dispatch(
      setSelectedByAttFilter({
        id: filterParams,
        name: attFilterEntries.map(ent => ent.inventory?.name || ""),
        open: true,
        type:
          bookingType === BookingType.CONFERENCEZONE
            ? SelectedZoneOrWorkplaceType.ZONE
            : SelectedZoneOrWorkplaceType.WORKPLACE,
        floorInventoryId: floorId,
        bookingType: bookingType
      })
    );
  } else {
    // if none selected by att filter then reset
    dispatch(
      setSelectedByAttFilter({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: -1,
        bookingType: undefined
      })
    );
  }
};
